<template>
  <Dialog
    header="Classes"
    :draggable="false"
    :visible="isModalVisible"
    :modal="true"
    :style="{ width: '50vw' }"
    @update:visible="handleClose"
  >
    <VdForm :model="form" :loading="queryLoading" readonly>
      <div v-if="classChoices.length" class="class-selection">
        <VdFormField prop="class">
          <VdSelect v-model="selectedClassTechnicalName" :choices="classChoices" placeholder="Add a class" multiple>
            <template #item="{ item }">
              <div :class="['class-selection-option', { locked: item.locked }]">
                <VdTooltip
                  v-if="!!item.locked"
                  :content="`This class is already used by the layer '${item.locked}'`"
                  orientation="right"
                  alignment="end"
                >
                  <VdIcon store="fa" name="lock" />
                  <span>{{ item.label }}</span>
                </VdTooltip>
                <span v-else>{{ item.label }}</span>
              </div>
            </template>
          </VdSelect>
        </VdFormField>
      </div>
      <div v-for="override in form.treatments" :key="override.classId" style="margin-bottom: 1rem">
        <!-- VT.ShopManagementClass must be hidden in the UI -->
        <CohortFormClassItem
          v-if="override.classId !== 'VT.ShopManagementClass'"
          :cohort-id="cohortId"
          :clazz="classesByTechnicalName[override.classId]"
          :override-instances="override.instances"
          :model-value="override"
          :can-delete-class="false"
          :overridden-values="instanceValuesByClass[override.classId]"
          @update:model-value="updateOverriddenClassValues"
          @delete="() => onDeleteClass(override.classId)"
        />

        <div v-if="classesByTechnicalName[override.classId].dynamicInstances" class="add-instance-button-wrapper">
          <vd-button
            type="secondary"
            skin="blue"
            icon-left="fa:add"
            class="add-instance-button"
            @click="onAddInstance(override.classId)"
          >
            Add instance
          </vd-button>
        </div>
      </div>
    </VdForm>
    <template #footer>
      <Button>
        <span @click="handleClose">Close</span>
      </Button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { VdFormField, VdSelect, VdTooltip, VdForm } from "@voodoo.io/voodoo-ui";
import { computed, nextTick, reactive, ref, watch } from "vue";

import { v4 as uuidv4 } from "uuid";

import { CohortInputOverride, useCohortFormPageQuery } from "@/graphql";
import useFormTracking from "@/hooks/form-tracking";
import useMessages from "@/hooks/messages";
import { objectifyArray } from "@/utils";

import CohortFormClassItem from "../../pages/games/GameDisplay/children/abtests/CohortsList/children/CohortForm/components/CohortFormClassItem/CohortFormClassItem.vue";

const { confirm } = useMessages();
const selectedTab = ref("");

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  abtestId: {
    type: String,
    required: true,
  },
  cohortId: {
    type: String,
    required: true,
  },
  segmentId: {
    type: String,
    required: true,
  },
  classId: {
    type: String,
    default: "",
  },
  versionId: {
    type: String,
    required: true,
  },
  redirectTo: {
    type: String,
    default: "",
  },
  isVisible: {
    type: Boolean,
    default: true,
  },
});

const emits = defineEmits(["close"]);

const handleClose = () => {
  emits("close");
};
const isModalVisible = ref(props.isVisible);

interface DynamicCohortInput {
  name: string;
  description: string;
  allocationRate: number;
  treatments: CohortInputOverride[];
}

/**
 * Define the form
 */
const form = reactive<DynamicCohortInput>({
  name: "",
  description: "",
  allocationRate: 0,
  treatments: [],
});

/**
 * Enable form tracking
 */
const { resetTracking: resetFormTracking } = useFormTracking(form);

/**
 * Try to fetch the cohort from its id.
 * If we have a cohort, update the form title and fill the form
 */
const { result, loading: queryLoading } = useCohortFormPageQuery({
  gameId: props.gameId,
  versionId: props.versionId,
  abtestId: props.abtestId,
});

const cohorts = computed(() => result.value?.game?.version?.abtest?.cohorts ?? []);
const layerId = computed(() => result.value?.game?.version?.abtest?.layerId ?? undefined);
const layers = computed(() => result.value?.game?.version?.layers ?? []);
const cohort = computed(() => cohorts.value.find(({ id }) => id === props.cohortId));
const classes = computed(() => result.value?.game?.version?.classes ?? []);
const treatment = computed(() => cohort.value?.treatments?.find((t) => t.segmentId === props.segmentId));

const classesByTechnicalName = computed(() =>
  Object.fromEntries(classes.value.map((clazz) => [clazz.technicalName, clazz]))
);

const instanceValuesByClass = computed(() => {
  if (!cohort.value || cohort.value?.treatments?.length === 0 || !treatment.value?.classesOverrides) {
    return {};
  }

  return objectifyArray(
    treatment.value?.classesOverrides.map((override) => ({
      technicalName: override.class.technicalName,
      instances: override.instances,
    })),
    ["technicalName", "instances", ["id", "values", ["technicalName", "*"]]]
  );
});

/**
 * Watch the cohort changes and fill the form with its values
 */
watch(
  cohort,
  (watchedCohort) => {
    if (watchedCohort) {
      form.name = watchedCohort.name;
      form.description = watchedCohort.description;
      form.allocationRate = watchedCohort.allocationRate;

      const currentTreatment = watchedCohort.treatments?.find((t) => t.segmentId === props.segmentId);

      // Init value for overridden classes
      form.treatments =
        currentTreatment?.classesOverrides?.map((override) => {
          const classTechnicalName = override.class.technicalName;

          return {
            classId: classTechnicalName,
            instances: override.instances.map((instance) => ({
              id: instance.id,
              enabled: instance.enabled ?? true,
              values: instance.values.map(({ technicalName: attrTechnicalName }) => {
                const attrValue = instanceValuesByClass.value[classTechnicalName][instance.id][attrTechnicalName];
                return {
                  technicalName: attrTechnicalName,
                  value: attrValue.value,
                };
              }),
            })),
          };
        }) ?? [];

      /* always display the first class when arriving on the form */
      if (form.treatments.length) {
        if (props.classId) {
          selectedTab.value = props.classId;
        } else {
          selectedTab.value = form.treatments[0].classId;
        }
      }

      resetFormTracking();
    }
  },
  {
    immediate: true,
  }
);

/*
 * Generate the class choices.
 * Prevent displaying the class if it is already present
 */
const classChoices = computed(() =>
  classes.value
    ?.filter(({ technicalName }) => !form.treatments?.find((item) => item.classId === technicalName))
    .filter(({ technicalName }) => technicalName !== "VT.ShopManagementClass") // Filter Shop class
    .map(({ displayName, technicalName, lockedByLayer }) => {
      /**
       * Checking if `lockedByLayer` info is present and if it matches the current layerId.
       * If it's the case then let's return the layer name, otherwise don't send the `locked` info
       */
      let disabled = false;

      const locked =
        !!lockedByLayer && lockedByLayer !== layerId.value
          ? layers.value.find((l) => l.id === lockedByLayer)?.name
          : undefined;
      disabled = (lockedByLayer && lockedByLayer !== layerId.value) || false;

      return {
        label: displayName,
        value: technicalName,
        locked,
        disabled,
      };
    })
);

/**
 * When a class is selected, the class is added to list
 */
const selectedClassTechnicalName = ref("");

watch(selectedClassTechnicalName, async (watchedSelectedClassTechnicalName) => {
  const firstWatchedValue = watchedSelectedClassTechnicalName[0];
  if (!firstWatchedValue) {
    return;
  }
  const selectedClass = classes.value.find(({ technicalName }) => technicalName === firstWatchedValue);

  if (!selectedClass) {
    throw new Error(`unable to find the class ${firstWatchedValue}`);
  }
  const overriddenClass = instanceValuesByClass.value[selectedClass.technicalName];

  form.treatments = [
    ...form.treatments,
    {
      classId: selectedClass.technicalName,
      instances: selectedClass.instances.map((instance) => {
        const overriddenInstance = overriddenClass ? overriddenClass[instance.id] : null;
        const instanceDefaultValuesByAttribute = Object.fromEntries(
          instance.values.map(({ technicalName, defaultValue }) => [technicalName, defaultValue])
        );
        return {
          id: instance.id,
          enabled: instance.enabled,
          values: instance.values.map(({ technicalName: attrTechnicalName }) => {
            const overriddenValue = overriddenInstance
              ? overriddenInstance[attrTechnicalName].value
              : instanceDefaultValuesByAttribute[attrTechnicalName];
            return {
              technicalName: attrTechnicalName,
              value: overriddenValue,
            };
          }),
        };
      }),
    },
  ];

  selectedTab.value = selectedClass.technicalName;
  await nextTick();
  selectedClassTechnicalName.value = "";
});

/**
 * Remove the class from the list
 */
const onDeleteClass = async (technicalName: string) => {
  const confirmed = await confirm("Do you really want to remove this class from the list?");
  if (confirmed) {
    form.treatments = form.treatments.filter((item) => item.classId !== technicalName);
    if (form.treatments.length) {
      selectedTab.value = form.treatments[form.treatments.length - 1].classId;
    }
  }
};

const updateOverriddenClassValues = (value: CohortInputOverride) => {
  form.treatments = form.treatments.map((item) => (item.classId === value.classId ? value : item));
};

const onAddInstance = (id: string) => {
  form.treatments = (form?.treatments || []).map((item) =>
    item.classId === id
      ? {
          ...item,
          instances: [
            ...item.instances,
            {
              id: uuidv4(),
              enabled: true,
              values: classesByTechnicalName.value[id].attributes.map(
                ({ technicalName, isArrayType, formFieldType }) => {
                  let value: string | boolean | string[] = isArrayType ? [] : "";
                  value = formFieldType === "boolean" ? false : value;
                  return {
                    technicalName,
                    value,
                  };
                }
              ),
            },
          ],
        }
      : item
  );
};
</script>

<style lang="scss">
.class-selection-option {
  &.locked {
    color: $red-alert;

    svg {
      color: $red-alert;
      margin-right: size("XS");
    }
  }
}

.add-instance-button-wrapper {
  background: var(--vd-color-text-light-9);
  border: 1px solid var(--vd-color-text-light-7);
  border-top: none;
  padding: 0.5rem;
}

.cohort-form-page {
  .vd-error-component {
    margin-top: 30%;
    max-width: 100%;
  }

  .el-tabs--left .el-tabs__header.is-left {
    width: 20%;
    min-width: 200px;
  }

  .el-tabs--left .el-tabs__item.is-left {
    @include ellipsis;
  }

  .class-selection {
    display: flex;
    align-items: center;

    .add-class-button {
      margin-left: size("M");
    }
  }
  .shop-overrides-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .p-dropdown {
      width: 300px;
    }
  }
}

.footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
}
</style>
